import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { fetchRegions } from './api';
import type { PillarId } from './constants';
import type { RegionSummary } from './types';
import ArticleView from './components/ArticleView';
import ErrorBoundary from './components/ErrorBoundary';
import Feed from './components/Feed';
import Footer from './components/Footer';
import Header, { type ViewMode } from './components/Header';
import { useSettings } from './settings';
import { matchArticleId, navigate, usePath } from './lib/router';

/** Cosmos carries three.js; it is only downloaded when the toggle is used. */
const CosmosMode = lazy(() => import('./components/CosmosMode'));
const Intro = lazy(() => import('./components/Intro'));

const INTRO_KEY = 'iii:intro-seen';

/** Once per session, and never for readers who asked for less motion. */
function shouldShowIntro(): boolean {
  try {
    if (sessionStorage.getItem(INTRO_KEY) === '1') return false;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
  } catch {
    return false;
  }
  return true;
}

export default function App() {
  const path = usePath();
  const articleId = matchArticleId(path);
  const { t } = useSettings();

  const [mode, setMode] = useState<ViewMode>('classic');
  const [pillar, setPillar] = useState<PillarId>('news');
  const [subsection, setSubsection] = useState<string | null>(null);
  const [region, setRegion] = useState<string | null>(null);
  const [regions, setRegions] = useState<RegionSummary[]>([]);
  const [introOpen, setIntroOpen] = useState(shouldShowIntro);

  const dismissIntro = useCallback(() => {
    setIntroOpen(false);
    try {
      sessionStorage.setItem(INTRO_KEY, '1');
    } catch {
      // Private mode with storage disabled: intro simply shows again next load.
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchRegions(controller.signal)
      .then(setRegions)
      .catch(() => setRegions([]));
    return () => controller.abort();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [path]);

  const select = useCallback((nextPillar: PillarId, nextSubsection: string | null) => {
    setPillar(nextPillar);
    setSubsection(nextSubsection);
    setMode('classic');
    navigate('/');
    window.scrollTo(0, 0);
  }, []);

  const selectRegion = useCallback((nextRegion: string | null) => {
    setRegion(nextRegion);
    setMode('classic');
    navigate('/');
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="flex min-h-dvh flex-col bg-surface text-ink">
      {introOpen && (
        <ErrorBoundary fallback={null}>
          <Suspense
            fallback={
              <div className="fixed inset-0 z-50 grid place-items-center bg-surface">
                <button
                  type="button"
                  onClick={dismissIntro}
                  className="border border-rule px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-ink-muted"
                >
                  {t('close')}
                </button>
              </div>
            }
          >
            <Intro onDone={dismissIntro} />
          </Suspense>
        </ErrorBoundary>
      )}

      <Header
        regions={regions}
        activeRegion={region}
        onRegionChange={setRegion}
        pillar={pillar}
        subsection={subsection}
        onSelect={select}
        mode={mode}
        onModeChange={setMode}
      />

      <div className="flex-1">
        {articleId ? (
          <ArticleView key={articleId} id={articleId} />
        ) : mode === 'cosmos' ? (
          <ErrorBoundary>
            <Suspense
              fallback={
                <div className="grid min-h-[60vh] place-items-center font-mono text-[10px] uppercase tracking-[0.3em] text-ink-dim">
                  {t('loading')}
                </div>
              }
            >
              <CosmosMode />
            </Suspense>
          </ErrorBoundary>
        ) : (
          <Feed pillar={pillar} subsection={subsection} activeRegion={region} />
        )}
      </div>

      {mode === 'classic' && !articleId && (
        <Footer regions={regions} onSelectRegion={selectRegion} onSelect={select} />
      )}
    </div>
  );
}
